import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus controller for DSF Note forms with auto-calculated subtotals/totals.
 *
 * Usage: wrap each table section with data-dsf-note-calculator-target="section".
 * Detail inputs trigger recalculation on input.
 * Subtotal/total rows use disabled inputs with data-subtotal-sources or data-grand-total.
 *
 * Example:
 *   <input data-col="montant_brut" data-row="1" ...>              (detail)
 *   <input data-col="montant_brut" data-subtotal-sources="1,2,3"> (subtotal)
 *   <input data-col="montant_brut" data-grand-total="true">       (grand total)
 */
export default class extends Controller {
  static targets = ["section"]

  connect() {
    this.calculate()
  }

  calculate() {
    this.sectionTargets.forEach((section) => this.calculateSection(section))
  }

  calculateSection(section) {
    const allInputs = section.querySelectorAll("input[type='number']")
    // Index values by row + col
    const values = {}
    const subtotals = []
    const grandTotals = []

    allInputs.forEach((input) => {
      const col = input.dataset.col
      const row = input.dataset.row

      if (input.dataset.grandTotal !== undefined) {
        grandTotals.push(input)
      } else if (input.dataset.subtotalSources !== undefined) {
        subtotals.push(input)
      } else if (row && col) {
        values[`${row}_${col}`] = parseFloat(input.value) || 0
      }
    })

    // Calculate subtotals
    subtotals.forEach((input) => {
      const col = input.dataset.col
      const sources = input.dataset.subtotalSources.split(",")
      const total = sources.reduce(
        (sum, idx) => sum + (values[`${idx.trim()}_${col}`] || 0),
        0
      )
      input.value = total || ""
    })

    // Calculate grand totals (sum of subtotals for the same column)
    grandTotals.forEach((input) => {
      const col = input.dataset.col
      const sameColSubtotals = subtotals.filter((st) => st.dataset.col === col)
      const total = sameColSubtotals.reduce(
        (sum, st) => sum + (parseFloat(st.value) || 0),
        0
      )
      input.value = total || ""
    })
  }

  formatNumber(num) {
    if (!num) return ""
    return new Intl.NumberFormat("fr-FR").format(num)
  }
}
